import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  FileText, 
  TrendingUp, 
  Users, 
  Calendar, 
  Activity 
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell
} from "recharts";
import "../../Styling/DashReport.css";

const ChartDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { chartData, chartTitle, chartType, filters } = location.state || {};

  // State hooks
  const [selectedItems, setSelectedItems] = useState([]);
  const [chartView, setChartView] = useState('area');

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return "0.00";
    const num = parseFloat(amount);
    return isNaN(num) ? "0.00" : num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getChartIcon = () => {
    switch (chartType) {
      case 'revenue':
        return <TrendingUp size={24} className="chart-icon revenue" />;
      case 'customerSegments':
        return <Users size={24} className="chart-icon customers" />;
      case 'weeklySales':
        return <Calendar size={24} className="chart-icon sales" />;
      default:
        return <Activity size={24} className="chart-icon default" />;
    }
  };

  const getSummaryData = () => {
    if (!chartData || chartData.length === 0) return [];

    switch (chartType) {
      case 'revenue':
        const totalRevenue = chartData.reduce((sum, item) => sum + (item.revenue || 0), 0);
        const avgRevenue = totalRevenue / chartData.length;
        const maxRevenue = Math.max(...chartData.map(item => item.revenue || 0));
        const maxMonth = chartData.find(item => item.revenue === maxRevenue);
        return [
          { label: 'Total Revenue', value: `₹${formatAmount(totalRevenue)}` },
          { label: 'Average Monthly', value: `₹${formatAmount(avgRevenue)}` },
          { label: 'Highest Month', value: maxMonth ? `${maxMonth.month} - ₹${formatAmount(maxRevenue)}` : 'N/A' },
          { label: 'Months Tracked', value: chartData.length }
        ];
      
      case 'customerSegments':
        const totalAmount = chartData.reduce((sum, item) => sum + (item.amount || 0), 0);
        const totalCount = chartData.reduce((sum, item) => sum + (item.value || 0), 0);
        const topSegment = chartData.reduce((max, item) => 
          (item.amount || 0) > (max.amount || 0) ? item : max, chartData[0]
        );
        return [
          { label: 'Total Amount', value: `₹${formatAmount(totalAmount)}` },
          { label: 'Total Transactions', value: totalCount },
          { label: 'Top Segment', value: topSegment?.name || 'N/A' },
          { label: 'Segment Count', value: chartData.length }
        ];
      
      case 'weeklySales':
        const totalSales = chartData.reduce((sum, item) => sum + (item.amount || 0), 0);
        const avgWeekly = totalSales / chartData.length;
        const maxWeek = Math.max(...chartData.map(item => item.amount || 0));
        const maxWeekData = chartData.find(item => item.amount === maxWeek);
        return [
          { label: 'Total Sales', value: `₹${formatAmount(totalSales)}` },
          { label: 'Average Weekly', value: `₹${formatAmount(avgWeekly)}` },
          { label: 'Best Week', value: maxWeekData ? `Week ${maxWeekData.weekNumber} - ₹${formatAmount(maxWeek)}` : 'N/A' },
          { label: 'Weeks Tracked', value: chartData.length }
        ];
      
      default:
        return [];
    }
  };

  // Business Metrics Calculator - FIXED for all chart types
  const calculateMetrics = () => {
    if (chartType === 'revenue') {
      const values = chartData.map(item => item.revenue).filter(val => val > 0);
      if (values.length === 0) return {};
      
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const stdDev = Math.sqrt(values.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / values.length);
      
      return {
        cagr: values.length > 1 ? 
          (Math.pow(values[values.length - 1] / values[0], 1 / (values.length / 12)) - 1) * 100 : 0,
        volatility: (stdDev / mean) * 100,
        consistency: Math.max(0, (1 - (stdDev / mean)) * 100)
      };
    }
    
    if (chartType === 'customerSegments') {
      const totalAmount = chartData.reduce((sum, item) => sum + (item.amount || 0), 0);
      const segmentCount = chartData.length;
      const avgPerSegment = totalAmount / segmentCount;
      
      return {
        totalAmount: totalAmount,
        segmentCount: segmentCount,
        avgPerSegment: avgPerSegment
      };
    }
    
    if (chartType === 'weeklySales') {
      const values = chartData.map(item => item.amount).filter(val => val > 0);
      if (values.length === 0) return {};
      
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const stdDev = Math.sqrt(values.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / values.length);
      
      return {
        weeklyAvg: mean,
        volatility: (stdDev / mean) * 100,
        totalWeeks: values.length
      };
    }
    
    return {};
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.getElementById('chart-details-content');
      const canvas = await html2canvas(element, { 
        scale: 2,
        useCORS: true, 
        logging: false, 
        backgroundColor: "#ffffff" 
      });
      
      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`${chartTitle.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      alert("Failed to generate PDF");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Data Comparator Component - COMPLETELY FIXED selection issue
  const  DataComparator = () => {
    const toggleSelection = (item) => {
      const itemId = getItemId(item);
      const isCurrentlySelected = selectedItems.some(selected => getItemId(selected) === itemId);
      
      if (isCurrentlySelected) {
        // Remove from selection
        setSelectedItems(selectedItems.filter(selected => getItemId(selected) !== itemId));
      } else {
        // Add to selection (max 3 items)
        if (selectedItems.length < 3) {
          setSelectedItems([...selectedItems, item]);
        } else {
          alert('Maximum 3 items can be selected for comparison');
        }
      }
    };

    const getItemId = (item) => {
      return `${item.month || item.name || item.week}-${item.revenue || item.amount || 0}`;
    };

    const getItemDisplayValue = (item) => {
      return item.revenue || item.amount || 0;
    };

    const getItemDisplayName = (item) => {
      return item.month || item.name || item.week || 'Unknown';
    };

    const calculateComparison = () => {
      if (selectedItems.length < 2) return null;
      
      const comparisons = [];
      for (let i = 0; i < selectedItems.length; i++) {
        for (let j = i + 1; j < selectedItems.length; j++) {
          const itemA = selectedItems[i];
          const itemB = selectedItems[j];
          const valueA = getItemDisplayValue(itemA);
          const valueB = getItemDisplayValue(itemB);
          const difference = valueB - valueA;
          const percentage = valueA > 0 ? (difference / valueA) * 100 : 0;
          
          comparisons.push({
            from: getItemDisplayName(itemA),
            to: getItemDisplayName(itemB),
            difference,
            percentage,
            isPositive: difference > 0
          });
        }
      }
      
      return comparisons;
    };

    return (
      <div className="comparator-section">
        <h3>🔍 Compare Data Points</h3>
        <p>Select up to 3 items to compare (click to select/unselect)</p>
        
        <div className="selected-items-display">
          {selectedItems.length > 0 && (
            <div className="selected-count">
              Selected: {selectedItems.length}/3 items
            </div>
          )}
        </div>
        
        <div className="comparison-grid">
          {chartData.map((item, index) => {
            const isSelected = selectedItems.some(selected => getItemId(selected) === getItemId(item));
            
            return (
              <div
                key={index}
                className={`comparison-item ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleSelection(item)}
              >
                <div className="item-header">
                  <span className="item-name">{getItemDisplayName(item)}</span>
                  <div className="selection-indicator">
                    {isSelected ? '✓' : '+'}
                  </div>
                </div>
                <div className="item-value">
                  ₹{formatAmount(getItemDisplayValue(item))}
                </div>
                {item.percentage && (
                  <div className="item-percentage">{item.percentage}%</div>
                )}
              </div>
            );
          })}
        </div>
        
        {selectedItems.length >= 2 && (
          <div className="comparison-results">
            <h4>Comparison Results</h4>
            {calculateComparison()?.map((comp, index) => (
              <div key={index} className="comparison-result">
                <span className="comparison-label">{comp.from} → {comp.to}</span>
                <span className={comp.isPositive ? 'positive' : 'negative'}>
                  {comp.isPositive ? '↑' : '↓'} {Math.abs(comp.percentage).toFixed(1)}%
                  (₹{formatAmount(Math.abs(comp.difference))})
                </span>
              </div>
            ))}
          </div>
        )}
        
        {selectedItems.length === 0 && (
          <div className="no-selection-message">
            Click on items above to select them for comparison
          </div>
        )}
      </div>
    );
  };

// Detail Chart Renderer Component - FIXED curve visibility
const DetailChartRenderer = () => {
  const getChartDataKey = () => {
    switch (chartType) {
      case 'revenue': return 'revenue';
      case 'customerSegments': return 'amount';
      case 'weeklySales': return 'amount';
      default: return 'value';
    }
  };

  const getXAxisKey = () => {
    switch (chartType) {
      case 'revenue': return 'month';
      case 'customerSegments': return 'name';
      case 'weeklySales': return 'week';
      default: return 'name';
    }
  };

  // FIXED: Proper data formatting for curves
  const getFormattedData = () => {
    const dataKey = getChartDataKey();
    const xAxisKey = getXAxisKey();

    let formattedData = chartData
      .map(item => ({
        ...item,
        [dataKey]: parseFloat(item[dataKey]) || 0,
        // Ensure numeric values for proper curve rendering
        numericValue: parseFloat(item[dataKey]) || 0
      }));

    // Sort data for proper curve connection
    if (chartType === 'revenue') {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      formattedData = formattedData.sort((a, b) => {
        return monthOrder.indexOf(a[xAxisKey]) - monthOrder.indexOf(b[xAxisKey]);
      });
    } else if (chartType === 'weeklySales') {
      formattedData = formattedData.sort((a, b) => {
        return (a.weekNumber || 0) - (b.weekNumber || 0);
      });
    }

    return formattedData;
  };

  const renderChart = () => {
    const dataKey = getChartDataKey();
    const xAxisKey = getXAxisKey();
    const formattedData = getFormattedData(); // FIXED: Call the function to get the data

    // Debug log to verify data
    console.log('Formatted Chart Data:', formattedData);
    console.log('Chart Type:', chartType);
    console.log('Chart View:', chartView);

    // FIXED: Check if we have valid data
    if (!formattedData || formattedData.length === 0) {
      return (
        <div className="no-chart-data">
          <p>No data available for chart display</p>
        </div>
      );
    }

    switch (chartView) {
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={formattedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#486ada" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#486ada" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey={xAxisKey}
                tick={{ fill: '#555', fontSize: 12 }}
              />
              <YAxis 
                tick={{ fill: '#555', fontSize: 12 }}
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`₹${formatAmount(value)}`, 'Amount']}
                labelFormatter={(label) => `Period: ${label}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '10px'
                }}
              />
              <Area 
                type="monotone" // This creates the smooth curve
                dataKey={dataKey}
                stroke="#486ada"
                fill="url(#colorArea)"
                strokeWidth={3}
                fillOpacity={0.6}
                dot={{ 
                  fill: '#486ada', 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#ffffff'
                }}
                activeDot={{ 
                  r: 6, 
                  fill: '#486ada', 
                  stroke: '#ffffff', 
                  strokeWidth: 2 
                }}
                connectNulls={false}
                isAnimationActive={true}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={formattedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey={xAxisKey}
                tick={{ fill: '#555', fontSize: 12 }}
              />
              <YAxis 
                tick={{ fill: '#555', fontSize: 12 }}
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`₹${formatAmount(value)}`, 'Amount']}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <Bar 
                dataKey={dataKey}
                fill="#486ada"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={formattedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey={xAxisKey}
                tick={{ fill: '#555', fontSize: 12 }}
              />
              <YAxis 
                tick={{ fill: '#555', fontSize: 12 }}
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`₹${formatAmount(value)}`, 'Amount']}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <Line 
                type="monotone" // This creates the smooth curve
                dataKey={dataKey}
                stroke="#ff7300"
                strokeWidth={3}
                dot={{ 
                  fill: '#ff7300', 
                  strokeWidth: 2, 
                  r: 4,
                  stroke: '#ffffff'
                }}
                activeDot={{ 
                  r: 6, 
                  fill: '#ff7300', 
                  stroke: '#fff', 
                  strokeWidth: 2 
                }}
                connectNulls={false}
                isAnimationActive={true}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <div className="no-chart-data">
            <p>Select a chart type to view data</p>
          </div>
        );
    }
  };

  const getChartButtons = () => {
    return [
      { key: 'area', label: 'Area Chart', icon: '📈' },
      { key: 'radar', label: 'Bar Chart', icon: '📊' },
      { key: 'composed', label: 'Line Chart', icon: '📉' }
    ];
  };

  // FIXED: Get formatted data for the conditional check
  const formattedData = getFormattedData();

  return (
    <div className="detail-chart-section">
      <div className="chart-controls">
        {getChartButtons().map((button) => (
          <button 
            key={button.key}
            className={`chart-toggle-btn ${chartView === button.key ? 'active' : ''}`}
            onClick={() => setChartView(button.key)}
          >
            {button.icon} {button.label}
          </button>
        ))}
      </div>
      
      {formattedData && formattedData.length > 0 ? (
        renderChart()
      ) : (
        <div className="no-chart-data">
          <p>No data available for chart display</p>
        </div>
      )}
    </div>
  );
};

  if (!chartData) {
    return (
      <div className="details-container">
        <div className="details-header">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back to Dashboard
          </button>
        </div>
        <div className="no-data-message">
          <p>No data available to display</p>
        </div>
      </div>
    );
  }

  const summaryData = getSummaryData();
  const metrics = calculateMetrics();

  return (
    <div className="details-container">
      <div className="details-header">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={20} /> Back to Dashboard
        </button>
        <div className="details-title-section">
          <div className="title-with-icon">
            {getChartIcon()}
            <h1>{chartTitle}</h1>
          </div>
          <button className="download-btn pdf-btn" onClick={handleDownloadPDF}>
            <FileText size={20} /> Export to PDF
          </button>
        </div>
      </div>

      <div id="chart-details-content" className="details-content">
        {/* Filter Summary */}
        {filters && (
          <div className="filter-summary">
            <h3>Applied Filters</h3>
            <div className="filter-tags">
              {filters.orgId && <span className="filter-tag">Org ID: {filters.orgId}</span>}
              {filters.companyCode && <span className="filter-tag">Company: {filters.companyCode}</span>}
              {filters.fy && <span className="filter-tag">FY: {filters.fy}</span>}
              {filters.postingDate && <span className="filter-tag">Date: {filters.postingDate}</span>}
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summaryData.length > 0 && (
          <div className="details-summary">
            {summaryData.map((item, index) => (
              <div key={index} className="summary-card">
                <h3>{item.label}</h3>
                <p className="summary-value">{item.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* BUSINESS METRICS - FIXED for all chart types */}
        <div className="business-metrics">
          <h3> Business Metrics</h3>
          <div className="metrics-grid">
            {chartType === 'revenue' && (
              <>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.cagr ? `${metrics.cagr.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="metric-label">Annual Growth Rate</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.volatility ? `${metrics.volatility.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="metric-label">Revenue Volatility</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.consistency ? `${metrics.consistency.toFixed(0)}%` : 'N/A'}
                  </div>
                  <div className="metric-label">Performance Consistency</div>
                </div>
              </>
            )}
            
            {chartType === 'customerSegments' && (
              <>
                <div className="metric-card">
                  <div className="metric-value">
                    ₹{formatAmount(metrics.totalAmount || 0)}
                  </div>
                  <div className="metric-label">Total Amount</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.segmentCount || 0}
                  </div>
                  <div className="metric-label">Segment Count</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    ₹{formatAmount(metrics.avgPerSegment || 0)}
                  </div>
                  <div className="metric-label">Avg per Segment</div>
                </div>
              </>
            )}
            
            {chartType === 'weeklySales' && (
              <>
                <div className="metric-card">
                  <div className="metric-value">
                    ₹{formatAmount(metrics.weeklyAvg || 0)}
                  </div>
                  <div className="metric-label">Weekly Average</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.volatility ? `${metrics.volatility.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="metric-label">Sales Volatility</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">
                    {metrics.totalWeeks || 0}
                  </div>
                  <div className="metric-label">Weeks Tracked</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* INTERACTIVE CHART - NEW chart types */}
        <DetailChartRenderer />

        {/* DATA COMPARATOR - COMPLETELY FIXED */}
        <DataComparator />

        {/* Data Table Section */}
        <div className="details-table-section">
          <h2>Detailed Data</h2>
          {chartData.length > 0 ? (
            <div className="table-scroll-container">
              <table className="styled-table detailed-table">
                <thead>
                  <tr>
                    {Object.keys(chartData[0])
                      .filter(key => key !== 'color')
                      .map((key) => (
                        <th key={key}>
                          {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                        </th>
                      ))
                    }
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((item, index) => (
                    <tr key={index}>
                      {Object.entries(item)
                        .filter(([key]) => key !== 'color')
                        .map(([key, value], cellIndex) => (
                          <td key={cellIndex}>
                            {typeof value === 'number' ? 
                              (key.toLowerCase().includes('amount') || 
                               key.toLowerCase().includes('revenue') || 
                               key.toLowerCase().includes('value') ||
                               key.toLowerCase().includes('percentage') ? 
                                (key.toLowerCase().includes('percentage') ? 
                                  `${value}%` : `₹${formatAmount(value)}`
                                ) : value
                              ) : value
                            }
                          </td>
                        ))
                      }
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <p>No detailed data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartDetailsPage;